import { strToU8, unzipSync, zipSync } from "fflate";
import type { AssetVariant, PhotoFolioProject } from "../domain/project";
import { normalizeProject } from "../domain/project";
import { db } from "../storage/database";
import { readAssetFile, writeAssetFile } from "../storage/asset-store";

const variants: AssetVariant[] = ["original", "preview", "thumbnail"];

export async function createProjectArchive(project: PhotoFolioProject) {
  const files: Record<string, Uint8Array> = {
    "manifest.json": strToU8(JSON.stringify({
      format: "photofolio-project",
      schemaVersion: 1,
      createdWith: "PhotoFolio",
      createdAt: new Date().toISOString(),
    }, null, 2)),
    "project.json": strToU8(JSON.stringify(project, null, 2)),
    "licenses/open-source-notices.txt": strToU8("PhotoFolio uses Dexie, exifr, Pica, dnd-kit, fflate, React and Phosphor Icons. See the project README for license links."),
  };
  const assets = await db.assets.where("projectId").equals(project.id).toArray();
  await Promise.all(assets.flatMap((asset) => variants.map(async (variant) => {
    const blob = await readAssetFile(project.id, asset.id, variant);
    files[`assets/${asset.id}/${variant}`] = new Uint8Array(await blob.arrayBuffer());
  })));
  files["assets/metadata.json"] = strToU8(JSON.stringify(assets, null, 2));
  return new Blob([zipSync(files, { level: 6 }) as BlobPart], { type: "application/zip" });
}

export async function importProjectArchive(file: File) {
  const archive = unzipSync(new Uint8Array(await file.arrayBuffer()));
  if (!archive["manifest.json"] || !archive["project.json"]) throw new Error("不是有效的 PhotoFolio 草稿");
  const manifest = JSON.parse(new TextDecoder().decode(archive["manifest.json"]));
  if (manifest.format !== "photofolio-project") throw new Error("草稿格式不正确");
  const project = normalizeProject(JSON.parse(new TextDecoder().decode(archive["project.json"])));
  const assets = JSON.parse(new TextDecoder().decode(archive["assets/metadata.json"] || strToU8("[]")));
  await db.assets.bulkPut(assets);
  await Promise.all(assets.flatMap((asset: { id: string; mimeType: string }) => variants.map(async (variant) => {
    const bytes = archive[`assets/${asset.id}/${variant}`];
    if (!bytes) return;
    const type = variant === "original" ? asset.mimeType : "image/webp";
    await writeAssetFile(project.id, asset.id, variant, new Blob([bytes as BlobPart], { type }));
  })));
  await db.projects.put(project);
  return project;
}
